import React, { useState } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  UserCog, 
  Wrench, 
  Calendar, 
  CalendarClock,
  DollarSign,
  FileText,
  UserPlus,
  QrCode,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  Home,
  MapPin,
  Handshake
} from 'lucide-react';
import { Button } from './ui/button';

// roles: undefined = all, ['admin'] = admin only, etc.
const navItems = [
  { path: '/sistema', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/sistema/leads', label: 'Leads', icon: UserPlus, roles: ['admin', 'atendente'] },
  { path: '/sistema/agendamento', label: 'Agendamento', icon: Calendar, roles: ['admin', 'atendente'] },
  { path: '/sistema/grade', label: 'Grade Operacional', icon: CalendarClock, roles: ['admin', 'atendente'] },
  { path: '/sistema/clientes', label: 'Clientes', icon: Users, roles: ['admin', 'atendente'] },
  { path: '/sistema/colaboradores', label: 'Colaboradores', icon: UserCog, roles: ['admin'] },
  { path: '/sistema/parceiros', label: 'Parceiros', icon: Handshake, roles: ['admin'] },
  { path: '/sistema/servicos', label: 'Serviços', icon: Wrench, roles: ['admin', 'atendente'] },
  { path: '/sistema/cidades-atendimento', label: 'Cidades', icon: MapPin, roles: ['admin'] },
  { path: '/sistema/financeiro', label: 'Financeiro', icon: DollarSign, roles: ['admin', 'financeiro'] },
  { path: '/sistema/pix', label: 'PIX', icon: QrCode, roles: ['admin', 'financeiro'] },
  { path: '/sistema/usuarios', label: 'Usuários', icon: ShieldCheck, roles: ['admin'] },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-4 left-4 z-50 md:hidden bg-[#3D4F5F] text-white p-2 rounded-lg"
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`sidebar ${sidebarOpen ? 'open' : ''} md:translate-x-0`}
        data-testid="sidebar"
      >
        {/* Logo header */}
        <div className="flex-shrink-0 p-4">
          <Link to="/">
            <img src="/logo-full.png" alt="Tenance" className="h-10" />
          </Link>
          <p className="text-xs text-gray-400 mt-1">Sistema de Gestão</p>
        </div>

        {/* Scrollable navigation */}
        <nav className="sidebar-nav py-1">
          {navItems
            .filter((item) => !item.roles || item.roles.includes(user?.role))
            .map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/sistema'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'active' : ''}`
              }
              style={({ isActive }) => ({
                borderLeftColor: isActive ? '#2B7A3D' : 'transparent'
              })}
              data-testid={`nav-${item.path.split('/').pop() || 'dashboard'}`}
            >
              <item.icon size={18} />
              <span className="text-sm">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer: back to site + user + logout */}
        <div className="sidebar-footer">
          <Link 
            to="/" 
            className="flex items-center gap-2 px-5 py-3 text-gray-400 hover:text-white hover:bg-white/5 transition-colors text-sm border-b border-white/5"
            data-testid="back-to-site-btn"
          >
            <Home size={16} />
            Voltar ao site
          </Link>
          <div className="px-4 py-3">
            <p className="text-xs text-gray-400">Logado como</p>
            <p className="text-sm font-medium text-white truncate" title={user?.email}>{user?.email}</p>
          </div>
          <div className="px-3 pb-3">
            <Button
              variant="ghost"
              className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut size={16} className="mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="main-content p-6 md:p-8" data-testid="main-content">
        {children}
      </main>
    </div>
  );
};

export default Layout;
